import { FlexColumn, FlexRow, MaterialIcon, StyledSearchBar } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { OnChangeParams } from 'baseui/select';
import { memo, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { ORDER_ASCENDING, ORDER_DESCENDING } from '../../constants/app';
import { CURRENT_STATE_SORT_OPTIONS } from '../../constants/currentStates';
import { useStyles } from '../../hooks/useStyles';
import { updateCurrentStatesState } from '../../storage/client';
import {
  filterGroupState,
  orderDirectionState,
  searchCurrentStateState,
  sortCurrentStateState,
  updatingCurrentStatesState,
} from '../../storage/currentStates';
import { allGroupsState } from '../../storage/groups';
import { CurrentStateSortOption } from '../../types/currentState';
import { InlineSelect } from '../ui/InlineSelect';
import { SortPicker } from '../ui/SortPicker';
import { StyledTooltip } from '../ui/StyledTooltip';

function CurrentStateFilters() {
  const { theme, css } = useStyles();
  const [filterGroup, setFilterGroup] = useRecoilState(filterGroupState);
  const [search, setSearch] = useRecoilState(searchCurrentStateState);
  const [sort, setSort] = useRecoilState(sortCurrentStateState);
  const [orderDirection, setOrderDirection] = useRecoilState(orderDirectionState);
  const resetFilterGroup = useResetRecoilState(filterGroupState);
  const resetSearch = useResetRecoilState(searchCurrentStateState);
  const resetSort = useResetRecoilState(sortCurrentStateState);
  const resetOrderDirection = useResetRecoilState(orderDirectionState);
  const groups = useRecoilValue(allGroupsState);
  const setUpdateCurrentStates = useSetRecoilState(updateCurrentStatesState);
  const updatingCurrentStates = useRecoilValue(updatingCurrentStatesState);

  const groupOptions = useMemo(() => {
    if (!groups) return;
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...groups.map((group) => ({ id: group._id, label: group.name })),
    ];
  }, [groups]);

  const onGroupChange = (params: OnChangeParams) => {
    params.option?.id ? setFilterGroup(params.option.id as string) : resetFilterGroup();
  };

  const onResetFilters = () => {
    resetFilterGroup();
    resetSort();
    resetOrderDirection();
    resetSearch();
  };

  const onSortChange = (params: OnChangeParams) => {
    params.option?.id ? setSort(params.option.id as CurrentStateSortOption) : resetSort();
  };

  const onOrderDirectionClick = () => {
    setOrderDirection((prev) => (prev === ORDER_ASCENDING ? ORDER_DESCENDING : ORDER_ASCENDING));
  };

  const onUpdate = () => {
    setUpdateCurrentStates((prev) => prev + 1);
  };

  return (
    <FlexColumn gap={theme.sizing.scale300}>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={css({ flex: 1 })}>
          <StyledSearchBar
            name={'current-state-search'}
            placeholder={'Buscar por identificador, placa o etiqueta'}
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <StyledTooltip
          content={'Actualizar'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onUpdate}
            kind="tertiary"
            isLoading={updatingCurrentStates}
          >
            <MaterialIcon name={'sync'} />
          </Button>
        </StyledTooltip>
      </FlexRow>
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={css({ flexWrap: 'wrap' })}
      >
        <SortPicker
          value={sort}
          options={CURRENT_STATE_SORT_OPTIONS}
          onOrderDirectionClick={onOrderDirectionClick}
          onSortChange={onSortChange}
          orderDirection={orderDirection}
        />
        {!!groups?.length && (
          <InlineSelect
            label="Grupo:"
            options={groupOptions}
            value={[{ id: filterGroup }]}
            onChange={onGroupChange}
          />
        )}
        <StyledTooltip
          content={'Restaurar filtros'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onResetFilters}
            kind="tertiary"
          >
            <MaterialIcon name={'recycling'} />
          </Button>
        </StyledTooltip>
      </FlexRow>
    </FlexColumn>
  );
}

export default memo(CurrentStateFilters);
